import axios from 'axios';
import { api } from './api';

export interface TemplateData {
  file: File;
}

export interface TemplateSingleData {
  image: File;
  brand: string;
  name: string;
  format: string;
  type: string;
}

export interface Template {
  id: number;
  data: {
    name: string;
    type: string;
    image: string;
    created_at: string;
  };
}

export interface TemplateResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: Template[];
}

export interface TemplateListParams {
  page?: number;
  page_size?: number;
  search?: string;
  ordering?: string;
  format_type?: string;
}

export interface GetTemplateDataResponse {
  image: string;
  brand: string;
  name: string;
  format_type: string;
  type: string;
  created_at: string;
}

export interface TemplateTableRow {
  id: number;
  name: string;
  brand?: string;
  format_type?: string;
  type: string;
  image: string;
  created_at: string;
}

export const templateService = {
  saveTemplate: async (
    templateData: TemplateData
  ): Promise<{ success: boolean; message: string }> => {
    try {
      const formData = new FormData();
      formData.append('file', templateData.file);

      const response = await api.post('/templates', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      return {
        success: true,
        message: response.data.message,
      };
    } catch (error) {
      console.error('Error al guardar template:', error);

      if (axios.isAxiosError(error)) {
        return {
          success: false,
          message: error.response?.data?.error || 'Error al comunicarse con el servidor',
        };
      }

      return {
        success: false,
        message: 'Error inesperado al guardar el template',
      };
    }
  },

  getTemplates: async (params?: TemplateListParams): Promise<TemplateResponse> => {
    try {
      const response = await api.get('/templates/list', { params });
      return response.data;
    } catch (error) {
      console.error('Error al obtener templates:', error);
      if (axios.isAxiosError(error)) {
        throw new Error(
          error.response?.data?.message || 'Error al comunicarse con el servidor'
        );
      }
      throw new Error('Error inesperado al obtener los templates');
    }
  },

  exportTemplates: async (templateIds?: (string | number)[], format: 'xlsx' | 'csv' = 'xlsx'): Promise<{ success: boolean; blob?: Blob; message?: string }> => {
    try {
      const body: Record<string, unknown> = { format };
      if (templateIds && templateIds.length > 0) {
        body.template_ids = templateIds;
      }

      const response = await api.post('/export-templates', body, { responseType: 'blob' });
      return { success: true, blob: response.data };
    } catch (error) {
      console.error('Error al solicitar exportación de templates:', error);
      if (axios.isAxiosError(error)) {
        return { success: false, message: error.response?.data?.error || 'Error al solicitar exportación' };
      }
      return { success: false, message: 'Error inesperado al solicitar exportación' };
    }
  },

  saveSingleTemplate: async (templateData: TemplateSingleData): Promise<any> => {
    const formData = new FormData();
    for (const key in templateData) {
      formData.append(key, templateData[key as keyof TemplateSingleData]);
    }
    try {
      const response = await api.post('/template', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return {
        success: true,
        message: response.data.message
      };
    } catch (error) {
      console.error('Error al guardar template:', error);
      if (axios.isAxiosError(error)) {
        return {
          success: false,
          message: error.response?.data?.error || 'Error al comunicarse con el servidor'
        };
      }
      return {
        success: false,
        message: 'Error inesperado al guardar el template'
      };
    }
  },

  updateTemplate: async (templateId: string, templateData: Omit<TemplateData, 'file'>): Promise<{ success: boolean; message: string }> => {
    try {
      const formData = new FormData();
      for (const key in templateData) {
        formData.append(key, templateData[key as keyof Omit<TemplateData, 'file'>]);
      }
      const response = await api.put(`/templates/${templateId}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return {
        success: true,
        message: response.data.message
      };
    } catch (error) {
      console.error('Error al actualizar template:', error);
      if (axios.isAxiosError(error)) {
        return {
          success: false,
          message: error.response?.data?.error || 'Error al comunicarse con el servidor'
        };
      }
      return {
        success: false,
        message: 'Error inesperado al actualizar el template'
      };
    }
  },

  deleteTemplate: async (templateId: string): Promise<{ success: boolean; message: string }> => {
    try {
      const response = await api.delete(`/templates/${templateId}`);
      return {
        success: true,
        message: response.data.message
      };
    } catch (error) {
      console.error('Error al eliminar template:', error);
      if (axios.isAxiosError(error)) {
        return {
          success: false,
          message: error.response?.data?.error || 'Error al comunicarse con el servidor'
        };
      }
      return {
        success: false,
        message: 'Error inesperado al eliminar el template'
      };
    }
  },

  getTemplate: async (templateId: string): Promise<{ success: boolean; message: string, data?: GetTemplateDataResponse }> => {
    try {
      const response = await api.get(`/templates/${templateId}`);
      const { format_type: format, ...rest } = response.data.data;
      return {
        success: true,
        message: response.data.message,
        data: { ...rest, format }
      };
    } catch (error) {
      console.error('Error al obtener template:', error);
      if (axios.isAxiosError(error)) {
        return {
          success: false,
          message: error.response?.data?.error || 'Error al comunicarse con el servidor'
        };
      }
      return {
        success: false,
        message: 'Error inesperado al obtener el template'
      };
    }
  },

  deleteTemplates: async (templateIds: string[]): Promise<{ success: boolean; message: string }> => {
    try {
      const response = await api.delete(`/templates/delete-multiple`, { data: { ids: templateIds } });
      return {
        success: true,
        message: response.data.message
      };
    } catch (error) {
      console.error('Error al eliminar templates:', error);
      if (axios.isAxiosError(error)) {
        return {
          success: false,
          message: error.response?.data?.error || 'Error al comunicarse con el servidor'
        };
      }
      return {
        success: false,
        message: 'Error inesperado al eliminar los templates'
      };
    }
  }
};
